import { MiniLoader, Button, Icon } from '@pwc/pwc-deals-ui';
import { get, isEmpty } from 'lodash';
import { useLiveReportPreview } from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/provider';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import _get from 'lodash/get';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SyncedInfo from 'common/components/Autosync/SyncedInfo.tsx';
import { getIsDarkModeSelector } from 'common/redux/userSettings/userSettingsSlice';
import { getContentColourConversion } from 'features/project/projectSlice';
import { isCustomColourConversionActive } from 'common/helpers/projectInitUtil';
import { useShouldDisplayReportPreview } from '../../../common/components/RibbonPanel/useShouldDisplayReportPreview.ts';
import UnableToLoadContent from '../common/UnableToLoadContent/UnableToLoadContent';
import { getSkipPowerBiReportTheme } from '../../project/projectSlice';
import {
  getPowerBiReport,
  getPowerBiReportSelector,
} from '../getPowerBiReportSlice';
import {
  getPowerBiReportsSelector,
  getPowerBiReports,
} from '../getPowerBiReportsSlice';
import {
  getReportPreviewPropsSelector,
  setReportPreviewProps,
  updateLibraryItem,
} from '../libraryItemsSlice';
import PowerBiReportDisplayConfig from './PowerBiReportDisplayConfig';
import {
  buildPowerBiReportConfig,
  computeContainerDimensions,
  openFullScreen,
} from './powerBiReportHelpers';
import './PowerBiReport.scss';
import {
  getIsThirdPartyMember,
  getUserRole,
} from '../../../common/redux/security/loggedInUser/loggedInUserSlice.js';

const PowerBiReport = ({
  item,
  projectId,
  handleReportData,
  handlePageSelect,
  handleRenderStatus,
  height,
  showAutoSync = true,
  predefinedPageName = '',
  hasContentError = false,
}) => {
  const dispatch = useDispatch();
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;

  const [isE2eEnvironment] = useState(Boolean(window.Cypress));
  const [reportData, setReportData] = useState(null);
  const powerBiContainerRef = useRef(null);
  const isDarkMode = useSelector(getIsDarkModeSelector);
  let skipPowerBiTheme = useSelector(getSkipPowerBiReportTheme);
  const contentColourConversion = useSelector(getContentColourConversion);
  const isCustomColourConversion = isCustomColourConversionActive(
    contentColourConversion
  );
  // skipping the powerBI color conversions when custom colour conversion is chosen
  skipPowerBiTheme = isCustomColourConversion ? true : skipPowerBiTheme;
  const [powerBiContainerDimensions, setPowerBiContainerDimensions] = useState({
    height: height || '650px',
    width: '100%',
  });
  const [reportId, setReportId] = useState();
  const [pageName, setPageName] = useState('');
  const [isPowerBiCalled, setIsPowerBiCalled] = useState(true);
  const { reportId: reportItemId } = item;
  const shouldDisplayReportPreview = useShouldDisplayReportPreview();
  const { isDisplayed } = useSelector(getReportPreviewPropsSelector) || {};
  const reportPreviewData = useLiveReportPreview();
  const { placeholderImage } = reportPreviewData;
  const { reports } = useSelector(getPowerBiReportsSelector);
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);

  const currentReport = reports?.filter((report) => {
    return report.reportId === reportId;
  })[0];

  const { loading, error, powerBiData } = useSelector(getPowerBiReportSelector);
  const powerBiRef = useRef();
  useEffect(() => {
    if (powerBiData) {
      const embedUrl = get(powerBiData, 'embedUrl', '');
      const embedToken = get(powerBiData, 'embedToken', '');
      const newReportId = get(powerBiData, 'reportId', '');

      if (newReportId !== reportId) return;

      setReportData({
        ...powerBiData,
        pageName: pageName !== undefined ? pageName : predefinedPageName,
        reportId: reportId || newReportId,
        embedUrl: embedUrl || undefined,
        embedToken: embedToken || undefined,
      });

      if (handleReportData) {
        handleReportData(powerBiData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [powerBiData]);

  useEffect(() => {
    dispatch(getPowerBiReports(projectId));
  }, [dispatch, projectId]);

  useEffect(() => {
    const { id } = item;
    // eslint-disable-next-line no-unused-expressions
    dispatch && dispatch(updateLibraryItem({ id, item: placeholderImage }));
    if (dispatch) {
      dispatch(
        setReportPreviewProps({
          isDirty: true,
        })
      );
    }
  }, [dispatch, item, placeholderImage]);

  useEffect(() => {
    const onDataLoad = async () => {
      if (
        (!item.data || isEmpty(item.data)) &&
        (!item.sharedContent || isEmpty(item.sharedContent)) &&
        projectId &&
        (item.id || item.reportId)
      ) {
        setReportId(item.reportId);
        setPageName(undefined);
        dispatch(getPowerBiReport({ projectId, item }));
      } else {
        let id;
        let name;
        if (item.isShared) {
          id = get(JSON.parse(item.data), 'ReportId', '');
          name = get(JSON.parse(item.data), 'PageName', '');
        } else if (item.sharedContent) {
          id = get(JSON.parse(item.sharedContent), 'ReportId', '');
          name = get(JSON.parse(item.sharedContent), 'PageName', '');
        } else {
          id = get(item, 'reportId', '');
          name = get(item, 'pageName', '');
        }

        if (id && isPowerBiCalled) {
          setReportId(id);
          setPageName(name);
          setIsPowerBiCalled(false);
          dispatch(getPowerBiReport({ projectId, item }));
        }
      }
    };
    onDataLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, reportItemId, shouldDisplayReportPreview, isDisplayed]);

  useEffect(() => {
    async function embedPowerBi() {
      if (error) return;

      window.powerbi.reset(powerBiContainerRef.current);
      window.powerbi.bootstrap(powerBiContainerRef.current, {
        type: 'report',
      });

      const embeddedReport = window.powerbi.embed(
        powerBiContainerRef.current,
        buildPowerBiReportConfig({
          embedToken: reportData.embedToken,
          embedUrl: reportData.embedUrl,
          pageName: reportData.pageName,
          reportId: reportData.reportId,
          isDarkMode,
          projectId,
          skipPowerBiTheme,
        })
      );

      embeddedReport.on('loaded', () => {
        embeddedReport.getPages().then((pages) => {
          const foundPage = pages.find(
            (page) =>
              _get(page, ['report', 'config', 'id'], null) ===
              reportData.reportId
          );

          if (foundPage) {
            const { height: pageHeight, width } = foundPage.defaultSize;
            const [computedHeight] = computeContainerDimensions(
              height || pageHeight,
              width,
              !!reportData.pageName
            );
            setPowerBiContainerDimensions({
              height: height || computedHeight + 20,
              width: '100%',
            });
          }

          if (handlePageSelect) {
            if (!reportData.pageName && pages[0]) {
              handlePageSelect({
                name: pages[0].name,
                displayName: pages[0].displayName,
              });
            }
          }

          if (handleRenderStatus) {
            handleRenderStatus(true);
          }

          embeddedReport.on('pageChanged', (event) => {
            const {
              detail: { newPage },
            } = event;

            if (handlePageSelect) {
              handlePageSelect({
                name: newPage.name,
                displayName: newPage.displayName,
              });
            }
          });
        });
      });
    }
    if (reportData && powerBiContainerRef.current) {
      embedPowerBi();
    }
  }, [
    reportData,
    isDarkMode,
    handlePageSelect,
    handleRenderStatus,
    height,
    error,
    skipPowerBiTheme,
    projectId,
  ]);

  const toggleFullscreenTest = () => {
    openFullScreen(powerBiRef.current);
  };
  const powerBiDisplay = !reportData ? 'none' : null;
  const hasErrorOccurred = hasContentError || error || item.hasError;

  return (
    <div className="power-bi-report-wrapper" ref={powerBiRef}>
      {loading && !hasErrorOccurred && (
        <div className="mini-loader-container-wrapper">
          <MiniLoader dataTestId="library-item-loading-indicator" />
        </div>
      )}

      {hasErrorOccurred && (
        <UnableToLoadContent
          projectId={projectId}
          placeId="content-error-powerBiReport"
          type={400}
        />
      )}
      {!item.isLoading && !hasErrorOccurred && (
        <div data-testid="powerbi-wrapper" className="powerbi-wrapper">
          {isE2eEnvironment && reportData && (
            <PowerBiReportDisplayConfig
              embedToken={reportData.embedToken}
              reportId={reportData.reportId}
              embedUrl={reportData.embedUrl}
              reportTitle={reportData.reportTitle}
              pageName={reportData.pageName}
            />
          )}

          <div
            className="powerbi-container"
            ref={powerBiContainerRef}
            data-testid="powerbi-container"
            style={
              showAutoSync && !isThirdPartyMember
                ? {
                    width: powerBiContainerDimensions.width,
                    display: powerBiDisplay,
                  }
                : {
                    width: powerBiContainerDimensions.width,
                    display: powerBiDisplay,
                  }
            }
          >
            Loading Power BI Report...
          </div>
        </div>
      )}
      {!item.isLoading && !loading && reportData && (
        <div className="sync-wrapper">
          {!item.isShared &&
            showAutoSync &&
            !hasErrorOccurred &&
            !isThirdPartyMember && (
              <SyncedInfo
                isPwcUser={isPwcUser}
                projectId={projectId}
                isPowerBi
                currentReport={currentReport}
              />
            )}
          {!hasErrorOccurred && (
            <span className="fullScreen-wrapper">
              <Button
                className="button-link fullScreen-button"
                onClick={toggleFullscreenTest}
              >
                <Icon
                  name="full-screen"
                  width={22}
                  height={22}
                  className="outline icon-orange-theme"
                />
                <span>View fullscreen</span>
              </Button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default PowerBiReport;
